import React, { Component } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";

const CREATE_VENDOR = gql`
  mutation ($input: VendorInput!) {
    createVendor(input: $input) {
      id
      vendor_name
    }
  }
`;

const UPDATE_VENDOR = gql`
  mutation ($input: VendorInput!) {
    updateVendor(input: $input) {
      id
      vendor_name
    }
  }
`;

const DELETE_VENDOR = gql`
  mutation ($id: ID!) {
    deleteVendor(id: $id) {
      id
    }
  }
`;

const THEME = {
  bgCard: "#ffffff",
  bgLight: "#f8fafc",
  primary: "#6366f1",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
  danger: "#ef4444",
};

class VendorManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      editItem: null,
      formData: {
        vendor_name: "",
        contact_person: "",
        contact_no: "",
        address: "",
        comments: ""
      },
      saving: false,
      errors: {},
      warnings: {}
    };
  }

  validateField = (name, value) => {
    const errors = { ...this.state.errors };
    const warnings = { ...this.state.warnings };

    if (name === "vendor_name") {
      if (!value || !value.trim()) {
        errors.vendor_name = "Vendor name is required";
      } else if (value.trim().length < 2) {
        errors.vendor_name = "Vendor name must be at least 2 characters";
      } else if (value.length > 45) {
        errors.vendor_name = "Vendor name cannot exceed 45 characters";
      } else if (!/^[a-zA-Z0-9 \.\-]+$/.test(value)) {
        errors.vendor_name = "Only letters, numbers, spaces, dots, hyphens";
      } else {
        delete errors.vendor_name;
      }
    }

    if (name === "contact_person") {
      if (!value || !value.trim()) {
        delete errors.contact_person;
      } else if (value.trim().length < 2) {
        errors.contact_person = "Contact person must be at least 2 characters";
      } else if (value.length > 45) {
        errors.contact_person = "Contact person cannot exceed 45 characters";
      } else if (!/^[a-zA-Z ]+$/.test(value)) {
        errors.contact_person = "Only letters and spaces allowed";
      } else {
        delete errors.contact_person;
      }
    }

    if (name === "contact_no") {
      if (!value || !value.trim()) {
        delete errors.contact_no;
        delete warnings.contact_no;
      } else {
        const cleaned = value.replace(/[\s\-]/g, "");
        if (!/^[+]?[0-9]{10,15}$/.test(cleaned)) {
          errors.contact_no = "Enter valid phone number (10-15 digits, digits only)";
          delete warnings.contact_no;
        } else {
          delete errors.contact_no;
          if (/^0/.test(cleaned) && !cleaned.startsWith("+")) {
            warnings.contact_no = "Looks like a local number — include country code if international";
          } else {
            delete warnings.contact_no;
          }
        }
      }
    }

    if (name === "address") {
      if (value && value.length > 100) {
        errors.address = "Address cannot exceed 100 characters";
      } else {
        delete errors.address;
      }
    }

    if (name === "comments") {
      if (value && value.length > 1000) {
        errors.comments = "Comments cannot exceed 1000 characters";
      } else {
        delete errors.comments;
      }
    }

    this.setState({ errors, warnings });
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.vendor_name || !formData.vendor_name.trim()) {
      errors.vendor_name = "Vendor name is required";
    } else if (formData.vendor_name.trim().length < 2) {
      errors.vendor_name = "Vendor name must be at least 2 characters";
    } else if (formData.vendor_name.length > 45) {
      errors.vendor_name = "Vendor name cannot exceed 45 characters";
    } else if (!/^[a-zA-Z0-9 \.\-]+$/.test(formData.vendor_name)) {
      errors.vendor_name = "Only letters, numbers, spaces, dots, hyphens";
    }

    if (formData.contact_person && formData.contact_person.trim()) {
      if (formData.contact_person.trim().length < 2) {
        errors.contact_person = "Contact person must be at least 2 characters";
      } else if (formData.contact_person.length > 45) {
        errors.contact_person = "Contact person cannot exceed 45 characters";
      } else if (!/^[a-zA-Z ]+$/.test(formData.contact_person)) {
        errors.contact_person = "Only letters and spaces allowed";
      }
    }

    if (formData.contact_no && formData.contact_no.trim()) {
      const cleaned = formData.contact_no.replace(/[\s\-]/g, "");
      if (!/^[+]?[0-9]{10,15}$/.test(cleaned)) {
        errors.contact_no = "Enter valid phone number (10-15 digits, digits only)";
      }
    }

    if (formData.address && formData.address.length > 100) {
      errors.address = "Address cannot exceed 100 characters";
    }

    if (formData.comments && formData.comments.length > 1000) {
      errors.comments = "Comments cannot exceed 1000 characters";
    }

    return errors;
  };

  vendorColumns() {
    return [
      {
        dataField: "id",
        text: "Sr. No",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "80px", textAlign: "center" },
        style: { textAlign: "center" }
      },
      {
        dataField: "vendor_name",
        text: "Vendor Name",
        sort: true,
        formatter: (cell) => <strong style={{ color: "#6366f1" }}>{cell}</strong>
      },
      {
        dataField: "contact_person",
        text: "Contact Person",
        sort: true,
        formatter: (cell) => cell || "—"
      },
      {
        dataField: "contact_no",
        text: "Contact Number",
        sort: true,
        formatter: (cell) => cell || "—"
      },
      {
        dataField: "address",
        text: "Office Address",
        sort: true,
        formatter: (cell) => cell || "—"
      },
      {
        dataField: "comments",
        text: "Comments / Notes",
        sort: true,
        formatter: (cell) => <span style={{ fontStyle: "italic", color: "#64748b" }}>{cell || "—"}</span>
      },
      {
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "120px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell, row) => (
          <div>
            <button
              onClick={() => this.handleOpenModal(row)}
              style={{ background: "none", border: "none", color: "#6366f1", marginRight: "12px", cursor: "pointer" }}
            >
              <i className="fal fa-edit" />
            </button>
            <button
              onClick={() => this.handleDelete(row.id, row.vendor_name)}
              style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}
            >
              <i className="fal fa-trash-alt" />
            </button>
          </div>
        )
      }
    ];
  }

  handleOpenModal = (vendor = null) => {
    if (vendor) {
      this.setState({
        showModal: true,
        editItem: vendor,
        formData: {
          vendor_name: vendor.vendor_name || "",
          contact_person: vendor.contact_person || "",
          contact_no: vendor.contact_no || "",
          address: vendor.address || "",
          comments: vendor.comments || ""
        },
        errors: {},
        warnings: {}
      });
    } else {
      this.setState({
        showModal: true,
        editItem: null,
        formData: {
          vendor_name: "",
          contact_person: "",
          contact_no: "",
          address: "",
          comments: ""
        },
        errors: {},
        warnings: {}
      });
    }
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, editItem: null, errors: {}, warnings: {} });
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "contact_no") {
      // Allow only numbers and optionally '+' at the start
      const allowed = value.replace(/[^0-9+]/g, "");
      this.setState(
        prev => ({ formData: { ...prev.formData, [name]: allowed } }),
        () => {
          this.validateField(name, allowed);
        }
      );
      return;
    }

    if (name === "contact_person") {
      // Allow only alphabets and spaces
      const allowed = value.replace(/[^a-zA-Z ]/g, "");
      this.setState(
        prev => ({ formData: { ...prev.formData, [name]: allowed } }),
        () => {
          this.validateField(name, allowed);
        }
      );
      return;
    }

    this.setState(
      prev => ({ formData: { ...prev.formData, [name]: value } }),
      () => {
        this.validateField(name, value);
      }
    );
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const { formData, editItem } = this.state;
    const { client, onUpdate } = this.props;
    const instituteId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");

    this.setState({ saving: true });
    try {
      if (editItem) {
        await client.mutate({
          mutation: UPDATE_VENDOR,
          variables: {
            input: {
              id: editItem.id,
              vendor_name: formData.vendor_name,
              contact_person: formData.contact_person,
              contact_no: formData.contact_no,
              address: formData.address,
              comments: formData.comments,
              institute_id: instituteId
            }
          }
        });
        alert("Vendor updated successfully!");
      } else {
        await client.mutate({
          mutation: CREATE_VENDOR,
          variables: {
            input: {
              vendor_name: formData.vendor_name,
              contact_person: formData.contact_person,
              contact_no: formData.contact_no,
              address: formData.address,
              comments: formData.comments,
              institute_id: instituteId
            }
          }
        });
        alert("Vendor added successfully!");
      }
      this.handleCloseModal();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error saving vendor:", err);
      alert("Failed to save vendor: " + err.message);
    }
    this.setState({ saving: false });
  };

  handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete vendor "${name}"?\nThis action cannot be undone.`)) {
      return;
    }
    const { client, onUpdate } = this.props;
    try {
      await client.mutate({
        mutation: DELETE_VENDOR,
        variables: { id }
      });
      alert("Vendor deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error deleting vendor:", err);
      alert("Failed to delete vendor: " + err.message);
    }
  };

  render() {
    const { vendors } = this.props;
    const { showModal, editItem, formData, saving, errors, warnings } = this.state;

    const thStyle = { padding: "12px 16px", fontSize: "13px", fontWeight: "600", color: THEME.textMuted, background: THEME.bgLight, borderBottom: `2px solid ${THEME.border}` };
    const tdStyle = { padding: "13px 16px", fontSize: "13px", borderBottom: `1px solid ${THEME.border}` };
    const labelStyle = { fontSize: "13px", fontWeight: "600", color: THEME.textMuted, marginBottom: "5px", display: "block" };

    const hasErrors = errors && Object.keys(errors).length > 0;

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <h5 style={{ margin: 0, fontWeight: "800", color: "#1f2937", fontSize: "18px" }}>
            <i className="fal fa-store mr-2" style={{ color: THEME.primary, background: "#eff6ff", padding: "8px", borderRadius: "8px" }} />
            Expense Vendors
          </h5>
          <button
            onClick={() => this.handleOpenModal()}
            style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", boxShadow: `0 4px 12px ${THEME.primary}33`, transition: "all 0.2s" }}
          >
            <i className="fal fa-plus mr-2" /> Add Vendor
          </button>
        </div>

        <div style={{ paddingTop: "8px" }}>
          <CardLessDataTable
            parentData={vendors}
            particlecolumns={this.vendorColumns()}
            search={true}
            name="expense_vendors_list"
            placeholder="Search Vendors..."
          />
        </div>

        {/* Add/Edit Vendor Bootstrap Modal */}
        <Modal show={showModal} onHide={this.handleCloseModal} centered backdrop="static">
          <Modal.Header closeButton style={{ borderBottom: `1px solid ${THEME.border}` }}>
            <Modal.Title style={{ fontWeight: "700", fontSize: "16px", color: THEME.text }}>
              {editItem ? "Edit Vendor Details" : "Register New Vendor"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body className="p-4">
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Vendor Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={this.handleChange}
                  placeholder="e.g. Acme Supplies Pvt Ltd"
                  style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                  required
                />
                {errors?.vendor_name && (
                  <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    ⛔ {errors.vendor_name}
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Contact Person</Form.Label>
                <Form.Control
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={this.handleChange}
                  placeholder="e.g. John Doe"
                  style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                />
                {errors?.contact_person && (
                  <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    ⛔ {errors.contact_person}
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Contact Number</Form.Label>
                <Form.Control
                  type="text"
                  name="contact_no"
                  value={formData.contact_no}
                  onChange={this.handleChange}
                  placeholder="e.g. +91 98765 43210"
                  style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                />
                {errors?.contact_no && (
                  <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    ⛔ {errors.contact_no}
                  </p>
                )}
                {warnings?.contact_no && (
                  <p className="field-warning" style={{ color: "#e65c00", fontSize: 12, marginTop: 4 }}>
                    ⚠️ {warnings.contact_no}
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Office Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="address"
                  value={formData.address}
                  onChange={this.handleChange}
                  maxLength={100}
                  placeholder="Enter complete postal address..."
                  style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                />
                {errors?.address && (
                  <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    ⛔ {errors.address}
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Internal Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="comments"
                  value={formData.comments}
                  onChange={this.handleChange}
                  maxLength={1000}
                  placeholder="e.g. Primary stationery supplier..."
                  style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <div>
                    {errors?.comments && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, margin: 0 }}>
                        ⛔ {errors.comments}
                      </p>
                    )}
                  </div>
                  <small style={{ color: (formData.comments || "").length > 900 ? ((formData.comments || "").length === 1000 ? "red" : "orange") : "#888", fontWeight: "600" }}>
                    {(formData.comments || "").length} / 1000
                  </small>
                </div>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: `1px solid ${THEME.border}` }}>
              <Button variant="light" onClick={this.handleCloseModal} disabled={saving} style={{ fontWeight: "700" }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={saving || hasErrors} style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: "none", fontWeight: "700", padding: "8px 24px" }}>
                {saving ? <><Spinner size="sm" animation="border" className="mr-1" /> Saving...</> : "Save Vendor"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  }
}

export default VendorManagement;
